import { useState } from 'react';
import usePageMeta from '@/hooks/usePageMeta';

const SUBMIT_URL = 'https://readdy.ai/api/form/da1e2vhst185ek8asp8g';

export default function Contact() {
  usePageMeta({
    title: '联系我们',
    description: '联系漫画免费站，提交想看的漫画、找不到的资源、分类建议与阅读反馈。漫画免费全集目录聚合平台欢迎您的宝贵意见。',
    keywords: '漫画免费,联系我们,漫画反馈,漫画免费站',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Anti-spam honeypot
    const honeypot = String(data.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    data.delete('website_alt');

    setStatus('loading');
    setFormError('');

    const body = new URLSearchParams(data as unknown as Record<string, string>).toString();

    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = parsed?.code;
      const msg = String(meta?.message ?? parsed?.message ?? '');

      if (res.ok && code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(msg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试。');
    }
  };

  return (
    <main>
      <section className="bg-background-100 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <nav className="mb-2 text-sm text-foreground-500">
            <span>首页</span> <i className="ri-arrow-right-s-line" /> <span>联系我们</span>
          </nav>
          <h1 className="font-heading text-3xl text-foreground-950">联系我们</h1>
          <p className="mt-2 text-sm text-foreground-600">有想看的漫画、找不到的内容或任何建议，欢迎通过表单告诉我们。</p>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 md:px-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-900">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50"><i className="ri-mail-line" /></span>
                邮件联系
              </h2>
              <p className="mt-3 text-sm text-foreground-600">fankui@manhuamianfei.com</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-900">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50"><i className="ri-chat-3-line" /></span>
                反馈内容
              </h2>
              <ul className="mt-3 space-y-2 text-sm text-foreground-600">
                <li>· 想看的漫画名称</li>
                <li>· 找不到的资源</li>
                <li>· 分类与目录建议</li>
                <li>· 阅读体验反馈</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} data-readdy-form className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl text-foreground-900">提交反馈</h2>

              {/* anti-spam honeypot */}
              <div className="hp-guard" aria-hidden="true">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="您的称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">反馈类型</label>
                <select
                  id="topic"
                  name="topic"
                  required
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="">请选择</option>
                  <option value="想看的漫画">想看的漫画</option>
                  <option value="找不到的资源">找不到的资源</option>
                  <option value="分类与目录建议">分类与目录建议</option>
                  <option value="阅读体验反馈">阅读体验反馈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述您的需求或建议，最多 500 字"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              {status === 'error' && formError && (
                <div className="mt-4 flex items-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line" /> {formError}
                </div>
              )}
              {status === 'success' && (
                <div className="mt-4 flex items-center gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
                  <i className="ri-checkbox-circle-line" /> 感谢您的反馈，我们会尽快处理并回复！
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'loading' ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
                {status === 'loading' ? '提交中...' : '提交反馈'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}